import { useEffect, useMemo, useRef } from 'react';
import { getQueryKey } from './utils';
import { useMutation, useQueryClient } from '../query';
import { useOperationalStatus } from '../hooks';
import { mergeInfniteMessagesData } from './queryHelpers';
import { getConfig, isWidgetConfig } from '../config';
const maxMessagesToSync = 20;
export const useMessagesSync = (conversationId, options) => {
    const [operationsalStatus] = useOperationalStatus();
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => getQueryKey(conversationId), [conversationId]);
    const mutationKey = useMemo(() => ['conversations', conversationId, 'messages', 'sync'], [conversationId]);
    const { mutate: syncMessages } = useMutation({
        mutationKey,
        mutationFn: (seenMessages) => options.syncMessages(conversationId, seenMessages),
        onSuccess: data => {
            queryClient.setQueryData(queryKey, state => state
                ? // merge current state and new data
                    mergeInfniteMessagesData(state, data)
                : // create new state if there is no current state
                    {
                        pages: [data],
                        pageParams: [],
                    });
        },
    });
    const timerRef = useRef();
    useEffect(() => {
        timerRef.current = window.setInterval(() => {
            // do not sync messages if operational status is degraded
            if (operationsalStatus === 'degraded') {
                return;
            }
            const config = getConfig();
            // do not sync messages in guest mode
            if (isWidgetConfig(config) && config.identityType === 'guest') {
                return;
            }
            const mutationState = queryClient.getMutationCache().find({ mutationKey });
            // if query is in flight or failed, do not sync messages
            if (mutationState?.state.status === 'error' || mutationState?.state.status === 'pending') {
                return;
            }
            const queryData = queryClient.getQueryData(queryKey);
            if (!queryData) {
                return;
            }
            // take last page
            const lastPage = queryData.pages[0];
            // take last 20 messages
            const seenMessages = [...lastPage.messages]
                .slice(-maxMessagesToSync)
                .map(({ id, updatedAt }) => ({ id, updatedAt }));
            if (seenMessages.length === 0) {
                return;
            }
            syncMessages(seenMessages);
        }, 30_000);
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [mutationKey, operationsalStatus, queryClient, queryKey, syncMessages]);
};
//# sourceMappingURL=useMessagesSync.js.map